import React, { useEffect, useState, useRef } from "react";
import {
  Box, Button, Dialog, DialogActions, DialogContent, DialogTitle,
  TextField, Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, Paper, IconButton, TablePagination, Snackbar, Alert,
  Select, MenuItem, FormControl, InputLabel, Typography
} from "@mui/material";
import { Add, Edit, Delete, Undo, Today } from "@mui/icons-material";
import { axiosInstances } from "./apiConfig";

export default function ReportUI() {

  const [reports, setReports] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({});
  const [editing, setEditing] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const deleteTimers = useRef({});
  const [pendingDeletes, setPendingDeletes] = useState({});
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [total, setTotal] = useState(0);
  const [file, setFile] = useState(null);

  const loadReports = async (pageNum = page, size = rowsPerPage) => {
    try {
      const res = await axiosInstances.reportApi.get(`/?page=${pageNum}&size=${size}`);
      const payload = res.data.data || {};
      setReports(payload.data || []);
      setTotal(payload.total || 0);
    } catch (err) {
      console.error("Error fetching reports", err);
    }
  };

  useEffect(() => { loadReports(); }, [page, rowsPerPage]);

  const handleOpen = (report = {}) => {
    const transformedReport = {
      ...report,
      generatedAt: convertToInputDateFormat(report.generatedAt),
      fromDate: report.fromDate
        ? convertToInputDateFormat(report.fromDate)
        : new Date().toISOString().split("T")[0],
      toDate: report.toDate
        ? convertToInputDateFormat(report.toDate)
        : new Date().toISOString().split("T")[0],
    };
    setForm(transformedReport);
    setEditing(!!report.id);
    setOpen(true);
  };

  function formatDate(inputDate) {
    if (!inputDate) return "";
    const [year, month, day] = inputDate.split("-");
    return `${day}-${month}-${year}`;
  }

  function convertToInputDateFormat(dateStr) {
    if (!dateStr || !dateStr.includes("-")) return "";
    const [dd, mm, yyyy] = dateStr.split("-");
    return `${yyyy}-${mm}-${dd}`;
  }

  const handleClose = () => { setForm({}); setOpen(false); setFile(null);};

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };


  const handleSave = async () => {
    try {
      const token = localStorage.getItem("adminToken");

      if (file) {
        // ✅ Check file type
        const allowedTypes = [
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet", // .xlsx
          "application/vnd.ms-excel", // .xls
          "text/csv" // .csv
        ];

        if (!allowedTypes.includes(file.type)) {
          setSnackbar({ open: true, message: "Only Excel or CSV files are allowed for reports!", severity: "error" });
          return;
        }

        const formData = new FormData();
        formData.append("file", file);

        // ✅ Upload report file
        await axiosInstances.reportApi.post(`/uploadReports`, formData, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        setSnackbar({ open: true, message: "Report file uploaded successfully!", severity: "success" });

      } else {
        // ✅ Manual report submission
        const reportPayload = {
          ...form,
          generatedAt: formatDate(form.generatedAt),
          fromDate: formatDate(form.fromDate),
          toDate: formatDate(form.toDate),
        };

        if (editing) {
          await axiosInstances.reportApi.post(`/update`, reportPayload, {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
          });
          setSnackbar({ open: true, message: "Report updated successfully!", severity: "success" });
        } else {
          await axiosInstances.reportApi.post(`/save`, reportPayload, {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
          });
          setSnackbar({ open: true, message: "Report created successfully!", severity: "success" });
        }
      }

      loadReports();   // 🔁 Refresh report list
      handleClose();   // ❌ Close modal/dialog
      setFile(null);   // 🧹 Clear file input

    } catch (err) {
      if (err.response && err.response.status === 409) {
        setSnackbar({ open: true, message: "Duplicate report entry", severity: "error" });
      } else if (err.response && err.response.status === 401) {
        setSnackbar({ open: true, message: "Unauthorized - please log in as admin", severity: "error" });
      } else {
        setSnackbar({ open: true, message: "Something went wrong while saving the report!", severity: "error" });
      }
    }
  };

  const handleFileChange = (e) => {
    setFile(e.target.files[0]);
  };

  const handleDeleteClick = (id) => {
    setPendingDeletes((prev) => ({ ...prev, [id]: true }));
    deleteTimers.current[id] = setTimeout(async () => {
      try {
        await axiosInstances.reportApi.post(`/delete`, { id });
        loadReports();
      } catch (err) {
        console.error("Delete failed", err);
      } finally {
        setPendingDeletes((prev) => {
          const updated = { ...prev };
          delete updated[id];
          return updated;
        });
        deleteTimers.current[id] = null;
      }
    }, 30000);
  };

  const handleUndoClick = (id) => {
    if (deleteTimers.current[id]) {
      clearTimeout(deleteTimers.current[id]);
      deleteTimers.current[id] = null;
    }
    setPendingDeletes((prev) => {
      const updated = { ...prev };
      delete updated[id];
      return updated;
    });
  };

  const handleChangePage = (event, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <Box p={3}>
      <Typography variant="h4" gutterBottom>Report Management</Typography>
      <Button variant="contained" color="primary" startIcon={<Add />} onClick={() => handleOpen()}>
        Add Report
      </Button>

      <TableContainer component={Paper} sx={{ mt: 2 }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Sr. No.</TableCell>
              <TableCell>ID</TableCell>
              <TableCell>Report Name</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Generated By</TableCell>
              <TableCell>Generated At</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Format</TableCell>
              <TableCell>Period</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {reports.map((r, index) => (
              <TableRow key={r.id}>
                <TableCell>{page * rowsPerPage + index + 1}</TableCell>
                <TableCell>{r.id}</TableCell>
                <TableCell>{r.reportName}</TableCell>
                <TableCell>{r.reportType}</TableCell>
                <TableCell>{r.generatedBy}</TableCell>
                <TableCell>{r.generatedAt}</TableCell>
                <TableCell>{r.rpt_status}</TableCell>
                <TableCell>{r.format}</TableCell>
                <TableCell>{`${r.fromDate} to ${r.toDate}`}</TableCell>
                <TableCell>
                  <IconButton color="primary" onClick={() => handleOpen(r)}><Edit /></IconButton>
                  {pendingDeletes[r.id] ? (
                    <IconButton color="warning" onClick={() => handleUndoClick(r.id)}><Undo /></IconButton>
                  ) : (
                    <IconButton color="error" onClick={() => handleDeleteClick(r.id)}><Delete /></IconButton>
                  )}
                </TableCell>
              </TableRow>
            ))}
            {reports.length === 0 && (
              <TableRow>
                <TableCell colSpan={10} align="center">No reports found</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>

        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[5, 10, 25]}
          labelDisplayedRows={({ page }) => `Page ${page + 1} of ${Math.ceil(total / rowsPerPage)}`}
        />
      </TableContainer>

      {/* Dialog */}
      <Dialog open={open} onClose={handleClose}>
        <DialogTitle>{editing ? "Edit Report" : "Add Report"}</DialogTitle>
        <DialogContent>
          {editing && <TextField fullWidth margin="dense" label="Report ID" value={form.id || ""} disabled />}
          <TextField fullWidth margin="dense" label="Report Name" name="reportName" value={form.reportName || ""} onChange={handleChange} />
          <FormControl fullWidth margin="dense">
            <InputLabel>Type</InputLabel>
            <Select name="reportType" value={form.reportType || ""} onChange={handleChange}>
              <MenuItem value="SALES">Sales</MenuItem>
              <MenuItem value="AUDIT">Audit</MenuItem>
              <MenuItem value="INVENTORY">Inventory</MenuItem>
            </Select>
          </FormControl>
          <TextField fullWidth margin="dense" label="Generated By" name="generatedBy" value={form.generatedBy || ""} onChange={handleChange} />
          {/* <TextField fullWidth margin="dense" label="Generated At (dd-MM-yyyy)" name="generatedAt" value={form.generatedAt || ""} onChange={handleChange} /> */}
          <TextField
            fullWidth margin="dense" label="Generated At" name="generatedAt" type="date"
            value={form.generatedAt || new Date().toISOString().split("T")[0]} onChange={handleChange}
            InputLabelProps={{ shrink: true }}
          />
          <FormControl fullWidth margin="dense">
            <InputLabel>Status</InputLabel>
            <Select name="rpt_status" value={form.rpt_status || ""} onChange={handleChange}>
              <MenuItem value="COMPLETED">Completed</MenuItem>
              <MenuItem value="PENDING">Pending</MenuItem>
              <MenuItem value="FAILED">Failed</MenuItem>
            </Select>
          </FormControl>
          <FormControl fullWidth margin="dense">
            <InputLabel>Format</InputLabel>
            <Select name="format" value={form.format || ""} onChange={handleChange}>
              <MenuItem value="PDF">PDF</MenuItem>
              <MenuItem value="CSV">CSV</MenuItem>
              <MenuItem value="XLS">XLS</MenuItem>
            </Select>
          </FormControl>
          <TextField fullWidth margin="dense" label="File Path / URL" name="filePath" value={form.filePath || ""} onChange={handleChange} />
          <TextField fullWidth margin="dense" label="Description" name="description" value={form.description || ""} onChange={handleChange} multiline rows={3} />
          <TextField
            fullWidth margin="dense" label="From Date (dd-MM-yyyy)" name="fromDate" type="date"
            value={form.fromDate || new Date().toISOString().split("T")[0]} onChange={handleChange}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            fullWidth margin="dense" label="To Date (dd-MM-yyyy)" name="toDate" type="date"
            value={form.toDate || new Date().toISOString().split("T")[0]} onChange={handleChange}
            InputLabelProps={{ shrink: true }}
          />
          {!editing && (
            <>
              <Button
                variant="outlined"
                component="label"
                fullWidth
                sx={{ mt: 2 }}
              >
                Upload File
                <input
                  type="file"
                  hidden
                  onChange={handleFileChange}
                />
              </Button>
              {file && (
                <Typography variant="body2" sx={{ mt: 1 }}>
                  Selected: {file.name}
                </Typography>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button variant="contained" onClick={handleSave}>{editing ? "Update" : "Save"}</Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ width: "100%" }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
